import { createSlice } from '@reduxjs/toolkit';
import { isValidTournamentId } from 'utils/tournamentSubscriptions';

import { GameMode } from 'components/LobbyTable/LobbyTable';
import { TTournament } from 'components/TournamentTable/types';

export enum GameType {
  All = 'all',
  Texas = 'texas',
  Omaha = 'omaha'
}

export enum TableType {
  CashGames = 'cashGames',
  Standard = 'Standard',
  SitNGo = 'SitAndGo',
  Bounty = 'Bounty',
  FreezeOut = 'Freezeout'
}

export enum GameView {
  List = 0,
  Grid = 1
}

type ClosedCashGame = {
  gameID: string | number;
  gameType: GameType.Texas | GameType.Omaha | 'texas' | 'omaha';
};

const withoutGame = (games: any[], gameID: string) =>
  games.filter((game) => String(game?.gameID ?? game?.id) !== gameID);

const withoutGroupedGame = (groups: any[], gameID: string) =>
  groups
    .map((group) => {
      const tables = withoutGame(Array.isArray(group?.tables) ? group.tables : [], gameID);
      return { ...group, tables, tablesCount: tables.length };
    })
    .filter((group) => group.tablesCount > 0);

/**
 * The scheduled start of the tournament's current occurrence, per the lobby
 * list, or undefined when the list does not know it. A recurring tournament
 * keeps its tournamentId across occurrences; only startAtTime moves.
 */
const occurrenceStartAt = (tournamentList: any[], tournamentId: unknown) => {
  const entry = tournamentList.find((t: any) => Number(t?.tournamentId) === Number(tournamentId));
  const startAt = Number(entry?.startAtTime);
  return Number.isFinite(startAt) && startAt > 0 ? startAt : undefined;
};

const withOccurrence = (detail: any, tournamentList: any[]) => {
  const startAt = occurrenceStartAt(tournamentList, detail?.tournamentId);
  return startAt === undefined ? detail : { ...detail, occurrenceStartAt: startAt };
};

/**
 * EN-10562: a pre-start registration whose tournament has since moved to a new
 * occurrence. Cancellation deletes the registration and the tournament later
 * reschedules under the same id; if the feed never delivers that deletion, the
 * old record would otherwise mark the new occurrence REGISTERED until refresh.
 *
 * startAtTime also moves when a tournament starts (status P) and between the
 * flights of a multi-flight tournament, where players stay registered, so those
 * are excluded. An evicted record that is in fact current returns, stamped
 * with the new occurrence, on the next server push.
 */
const isRegistrationForPastOccurrence = (detail: any, tournamentList: any[]) => {
  if (detail?.status !== 'R' || detail?.occurrenceStartAt === undefined) return false;
  const entry = tournamentList.find(
    (t: any) => Number(t?.tournamentId) === Number(detail.tournamentId)
  );
  if (!entry || (entry.status !== 'A' && entry.status !== 'R')) return false;
  if (Number(entry.isMultiFlight) === 1 || Number(entry.isMultiFlightBreak) === 1) return false;
  const startAt = occurrenceStartAt(tournamentList, detail.tournamentId);
  return startAt !== undefined && startAt !== detail.occurrenceStartAt;
};

/**
 * How many consecutive legit-empty `updatePlayerTournamentDetails` pushes to
 * absorb before wiping non-empty state. The broadcaster reads from the Aurora
 * READ REPLICA (`sequelize.read`), which can transiently return `[]` for a
 * player with real records on the primary during replica lag. Two ticks
 * (~1–2s at the broadcaster's ~1 Hz cadence) is enough to absorb a single-tick
 * flicker without meaningfully delaying a real wipe. See the reducer for the
 * full reasoning.
 *
 * Exported for test injection and for documentation.
 */
export const EMPTY_PUSH_DEBOUNCE = 2;

export type TSelectedFilters = {
  stake: any[] | null;
  maxPlayer: any[] | null;
  minBuyIn: any[] | null;
  isHideEmptyGames: boolean;
};

interface IState {
  groupedCashGames: any[];
  texasCashGames: any[];
  groupedOmahaCashGames: any[];
  omahaCashGames: any[];
  hasFilter: boolean;
  sortingField: string | null;
  sortingDirection: string | null;
  tableViewSelected: GameView;
  selectedFilters: TSelectedFilters | null | undefined;
  tableTypeSelected: TableType;
  selectedGameMode: GameMode;
  selectedTournament: TTournament | null | undefined;
  gameTypeFiltered: any[];
  isLobbyReady: boolean;
  //tournament
  tournament: {
    tournamentList: any[];
    sitAndGoList: any[];
  };
  playerTournamentDetails: any[];
  /**
   * Consecutive legit-empty pushes seen since the last non-empty push. Used to
   * debounce a wipe of `playerTournamentDetails` — see the reducer for why.
   * Internal to the reducer; not consumed by any selector.
   */
  playerTournamentDetailsEmptyStreak: number;
}

const initialState: IState = {
  //cash games
  groupedCashGames: [],
  texasCashGames: [],
  groupedOmahaCashGames: [],
  omahaCashGames: [],
  hasFilter: false,
  sortingField: null,
  sortingDirection: 'asc',
  tableViewSelected: GameView.List,
  selectedFilters: null,
  tableTypeSelected: TableType.CashGames,
  gameTypeFiltered: [],
  isLobbyReady: false,
  selectedGameMode: GameMode.texas,
  //tournament
  tournament: {
    tournamentList: [],
    sitAndGoList: []
  },
  selectedTournament: null,
  playerTournamentDetails: [],
  playerTournamentDetailsEmptyStreak: 0
};

export const lobbySlice = createSlice({
  name: 'lobby',
  initialState,
  reducers: {
    updateTableView: (state, action) => {
      state.tableViewSelected = action.payload;
    },
    updateTableType: (state, action) => {
      state.tableTypeSelected = action.payload;
    },
    updateGameTypeFilter: (state, action) => {
      state.gameTypeFiltered = action.payload;
    },
    updateSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },
    updateGroupedCashGames: (state, action) => {
      state.groupedCashGames = action.payload;
    },
    updateTexasCashGames: (state, action) => {
      state.texasCashGames = action.payload;
    },
    updateGroupedOmahaCashGames: (state, action) => {
      state.groupedOmahaCashGames = action.payload;
    },
    updateOmahaCashGames: (state, action) => {
      state.omahaCashGames = action.payload;
    },
    removeClosedCashGame: (state, action: { payload: ClosedCashGame }) => {
      const gameID = String(action.payload.gameID);
      const isOmaha = action.payload.gameType === GameType.Omaha;

      if (isOmaha) {
        state.omahaCashGames = withoutGame(state.omahaCashGames, gameID);
        state.groupedOmahaCashGames = withoutGroupedGame(state.groupedOmahaCashGames, gameID);
      } else {
        state.texasCashGames = withoutGame(state.texasCashGames, gameID);
        state.groupedCashGames = withoutGroupedGame(state.groupedCashGames, gameID);
      }

      // This list is a cached projection used by the currently selected lobby
      // filters. Keeping the closed table here can leave its card visible even
      // after the variant-specific lists have been reconciled.
      state.gameTypeFiltered = withoutGame(state.gameTypeFiltered, gameID);
    },
    updateFilterState: (state, action) => {
      state.hasFilter = action.payload;
    },
    updateGameMode: (state, action) => {
      state.selectedGameMode = action.payload;
    },
    updateSelectedTournament: (state, action) => {
      state.selectedTournament = action.payload;
    },
    setSortingField: (state, action) => {
      state.sortingField = action.payload;
    },
    setSortingDirection: (state, action) => {
      state.sortingDirection = action.payload;
    },
    setTournamentList: (state, action) => {
      state.tournament.tournamentList = action.payload;
      const list = Array.isArray(action.payload) ? action.payload : [];
      if (state.playerTournamentDetails.length === 0) return;
      // Stamp records that arrived before their tournament was listed, then
      // evict any whose occurrence the list shows has been replaced.
      state.playerTournamentDetails = state.playerTournamentDetails
        .map((detail: any) =>
          detail?.occurrenceStartAt === undefined ? withOccurrence(detail, list) : detail
        )
        .filter((detail: any) => !isRegistrationForPastOccurrence(detail, list));
      if (state.playerTournamentDetails.length === 0) {
        state.playerTournamentDetailsEmptyStreak = 0;
      }
    },
    setSitAndGoList: (state, action) => {
      state.tournament.sitAndGoList = action.payload;
    },
    updatePlayerTournamentDetails: (state, action) => {
      // Drop the DEFAULT_TOURNAMENT_DETAILS sentinel (tournamentId: -1) that the
      // backend broadcasters emit ONLY when the tournament-details query returned
      // a non-array result (a broken result shape, treated as failure). It's a
      // Unity-era contract to signal "no tournament", not a real record — for
      // the web client it just flaps the subscribe set and pollutes downstream
      // consumers (count-based UI, redirect logic, etc.). Real records land
      // through untouched.
      const raw = Array.isArray(action.payload) ? action.payload : [];
      const real = raw.filter((d: any) => isValidTournamentId(d?.tournamentId));

      // Sentinel-only push (non-empty raw, empty after filter): the broadcaster
      // couldn't get an authoritative answer from the DB (query returned a
      // non-array). Preserve last-known state rather than wipe it on a transient
      // failure. Wiping evicts the Lobby's `subscribedThisLobbyMount` dedup entry
      // on the next Lobby tick and re-arms `SubscribeToGameUpdates` for the
      // same tournament — a smaller version of the ~1 Hz spam this branch
      // retired.
      if (raw.length > 0 && real.length === 0) return;

      // A legit non-empty push authoritatively replaces state, and resets the
      // empty-streak so the next empty push starts a fresh debounce.
      if (real.length > 0) {
        const list = state.tournament.tournamentList || [];
        state.playerTournamentDetails = real.map((detail: any) => withOccurrence(detail, list));
        state.playerTournamentDetailsEmptyStreak = 0;
        return;
      }

      // A legit empty push (`raw = []`) is authoritative in principle — that's
      // what lets a cancelled tournament's registration record be evicted once
      // the engine has deleted its `tournament_stats` rows (EN-10648, before
      // this fix a cancelled recurring tournament stayed marked "Registered"
      // until refresh; see the RCA at
      // docs/investigations/20260903-en-10648-stale-registration-after-cancelled-recurring-tournament.md).
      //
      // But `findTournamentPlayerDetailsByGUID` reads from `sequelize.read` —
      // the Aurora READ REPLICA — which can return [] for a player who has
      // real records on the primary during replica lag (crash recovery, catch-
      // up windows, ledger churn). If a single lagged read wiped state on
      // impact, the tile would flicker "Registered → Register → Registered"
      // and, briefly, `computeActiveTournamentRedirect*` would return null for
      // a player who really is seated. So the wipe is debounced: two consecutive
      // legit empties (~1s apart on the broadcaster) accepted before we act on
      // them. A single flicker between healthy pushes is absorbed.
      //
      // If state is ALREADY empty there is nothing to wipe, so we don't need
      // the streak — just no-op. The streak only matters as a guard on the
      // non-empty → empty transition.
      if (state.playerTournamentDetails.length === 0) {
        state.playerTournamentDetailsEmptyStreak = 0;
        return;
      }
      state.playerTournamentDetailsEmptyStreak += 1;
      if (state.playerTournamentDetailsEmptyStreak >= EMPTY_PUSH_DEBOUNCE) {
        state.playerTournamentDetails = [];
        state.playerTournamentDetailsEmptyStreak = 0;
      }
    },
    removePlayerTournamentDetail: (state, action) => {
      const tournamentId = Number(action.payload);
      if (!Number.isFinite(tournamentId)) return;

      // A successful unregister response is authoritative for this tournament.
      // Remove it immediately instead of waiting for the periodic details feed:
      // when this was the player's final registration, that feed sends the -1
      // sentinel above, which is intentionally ignored because it can also mean
      // a transient query failure.
      state.playerTournamentDetails = state.playerTournamentDetails.filter(
        (detail: any) => Number(detail?.tournamentId) !== tournamentId
      );
    },
    setLobbyReady: (state, action) => {
      state.isLobbyReady = action.payload;
    }
  }
});

export const {
  updateTableView,
  updateTableType,
  updateGameTypeFilter,
  updateSelectedFilters,
  updateGroupedCashGames,
  updateTexasCashGames,
  updateGroupedOmahaCashGames,
  updateOmahaCashGames,
  removeClosedCashGame,
  updateFilterState,
  updateGameMode,
  updateSelectedTournament,
  setSortingField,
  setSortingDirection,
  setTournamentList,
  setSitAndGoList,
  updatePlayerTournamentDetails,
  removePlayerTournamentDetail,
  setLobbyReady
} = lobbySlice.actions;

/**
 * Does an earlier lobby load leave anything worth painting right now?
 *
 * The lobby subtree is UNMOUNTED while the player is at a table
 * (SlideContainer), and both lobbies used to blank `isLobbyReady` on every
 * mount. Coming back — "Go to Lobby", the header logo, the "+" — therefore
 * threw away an already-loaded lobby and rendered nothing until the next
 * snapshot. LOBBY_DATA_RECEIVED is a server PUSH on a ~1s cadence, so that
 * was up to a second of the flat #12100c loading fill (Marlon QA 8/24).
 *
 * These lists are never cleared on the way to a table, so an in-session
 * return has real content to paint immediately and the next push replaces it
 * in place. A cold start still has nothing here, still blanks, and still gets
 * the splash.
 *
 * Pure and slice-scoped so it is unit-testable and so callers can read it
 * once off the store instead of subscribing — the lobby is the hottest
 * subtree in the app and this value is only ever read at mount.
 */
export const hasRenderableLobbyData = (lobby: IState): boolean =>
  lobby.groupedCashGames.length > 0 ||
  lobby.texasCashGames.length > 0 ||
  lobby.groupedOmahaCashGames.length > 0 ||
  lobby.omahaCashGames.length > 0 ||
  lobby.tournament.tournamentList.length > 0 ||
  lobby.tournament.sitAndGoList.length > 0;

export default lobbySlice.reducer;
